// 正文 HTML 解析
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'
import { buildHotspotTabsHtml, repairHotspotHtml } from '@/components/shared/utils/hotspotTabs'
import { buildEquipmentIntroHtml, repairEquipmentIntroHtml } from '@/components/shared/utils/equipmentIntro'
import { buildImageGalleryHtml, repairImageGalleryHtml } from '@/components/shared/utils/imageGallery'
import { parseHtmlFragment } from '@/components/shared/utils/promoteInlineStyles'
import {
  buildTableTabsHtml,
  countTableRows,
  DEFAULT_COLLAPSE_ROWS,
  normalizeTableStyle,
  tableStyleClassName,
  upgradeLegacyTablesInHtml,
  upgradeLegacyTablesInJson,
  wrapTableForCollapse,
} from '@/components/shared/utils/tableTabs'
import { wrapMathHtml } from '@/components/shared/utils/mathFormula'
import { getRichContentUiLabels } from '@/components/shared/utils/richContentUiLabels'
import {
  isProductCategoryLink,
  isProductDeviceLink,
  stripProductLinkPrefix,
  stripProductLinkPrefixInHtml,
} from '@/components/modules/products/utils/productHelpers'
import { isCopiedAbsoluteHref } from '@/components/shared/utils/copiedAbsoluteHref'
import { stripDisplayFlagsFromHtml } from '@/components/shared/utils/contentDisplayFlags'
import {
  splitSentenceHardBreaksInHtml,
  splitSentenceHardBreaksInJson,
} from '@/components/shared/utils/preserveParagraphGap'

const VIDEO_TEXT_RE = /^<video\s+[^>]*src=["']([^"']+)["'][^>]*><\/video>$/i
const LEGACY_DEFAULT_MARGIN_RE = /^(1\.2em|1\.20em|19\.2px|18px|1\.12em)$/i
const ZERO_MARGIN_RE = /^(0|0\.0+|0px|0\.0+px|0pt|0\.0+pt|0cm|0mm|0em|0rem|0%)$/i

function isUnsetParagraphMargin(value) {
  if (value == null) return true
  const text = String(value).trim().toLowerCase().replace(/\s+/g, '')
  if (!text) return true
  if (ZERO_MARGIN_RE.test(text)) return true
  return LEGACY_DEFAULT_MARGIN_RE.test(text)
}

/** 与 CMS 一致：零边距 / 旧 1.2em 不写进 style，走 CSS 默认 1em */
function normalizeParagraphSpacingValue(value) {
  if (value == null || value === '') return null
  const [marginTop = '', marginBottom = ''] = String(value).split('|')
  const nextTop = isUnsetParagraphMargin(marginTop) ? '' : String(marginTop || '').trim()
  const nextBottom = isUnsetParagraphMargin(marginBottom) ? '' : String(marginBottom || '').trim()
  if (!nextTop && !nextBottom) return null
  return `${nextTop}|${nextBottom}`
}

const SPACING_BLOCK_TYPES = new Set(['paragraph', 'heading'])

/** 与 CMS 打开编辑器时相同：JSON 里的零边距 / 旧 1.2em 改走 CSS 默认 1em */
function upgradeLegacyParagraphSpacingInJson(doc) {
  if (!doc || typeof doc !== 'object') return false
  let changed = false
  const walk = (node) => {
    if (!node || typeof node !== 'object') return
    if (SPACING_BLOCK_TYPES.has(node.type) && node.attrs?.paragraphSpacing) {
      const next = normalizeParagraphSpacingValue(node.attrs.paragraphSpacing)
      if (next !== node.attrs.paragraphSpacing) {
        node.attrs.paragraphSpacing = next
        changed = true
      }
    }
    const children = node.content
    if (Array.isArray(children)) children.forEach(walk)
  }
  walk(doc)
  return changed
}

const DEFAULT_FONT_PX = 16
const MIN_KEEP_LINE_HEIGHT = 1.5

function isWordDefaultLineHeight(value) {
  const text = String(value || '').trim().toLowerCase().replace(/\s+/g, '')
  if (!text) return true
  if (text === 'normal' || text === 'inherit' || text === 'unset' || text === 'initial') return true

  const percent = text.match(/^(-?[\d.]+)%$/)
  if (percent) {
    const n = Number(percent[1])
    return !Number.isFinite(n) || n < MIN_KEEP_LINE_HEIGHT * 100
  }

  const em = text.match(/^(-?[\d.]+)(em|rem)$/)
  if (em) {
    const n = Number(em[1])
    return !Number.isFinite(n) || n < MIN_KEEP_LINE_HEIGHT
  }

  const px = text.match(/^(-?[\d.]+)px$/)
  if (px) {
    const n = Number(px[1])
    return !Number.isFinite(n) || n / DEFAULT_FONT_PX < MIN_KEEP_LINE_HEIGHT
  }

  const pt = text.match(/^(-?[\d.]+)pt$/)
  if (pt) {
    const n = Number(pt[1])
    return !Number.isFinite(n) || (n * 4) / 3 / DEFAULT_FONT_PX < MIN_KEEP_LINE_HEIGHT
  }

  if (/^-?[\d.]+$/.test(text)) {
    const n = Number(text)
    return !Number.isFinite(n) || n < MIN_KEEP_LINE_HEIGHT
  }

  return false
}

function looksLikeZeroOrLegacyShorthand(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return false
  return parts.every((part) => isUnsetParagraphMargin(part))
}

/** 旧 HTML 里的零边距 / 旧默认段距清掉，让门户 CSS 的 1em 生效 */
function upgradeLegacyParagraphSpacingInHtml(html) {
  if (!html || !/margin/i.test(html)) return html
  try {
    const parsed = parseHtmlFragment(html, '__ps_up')
    if (!parsed?.root) return html
    const { root } = parsed
    let changed = false
    root.querySelectorAll('p, h1, h2, h3, h4, h5, h6').forEach((el) => {
      const style = el.style
      if (!style) return
      if (style.margin && looksLikeZeroOrLegacyShorthand(style.margin)) {
        style.margin = ''
        style.removeProperty('margin')
        changed = true
      }
      ;['marginTop', 'marginBottom', 'marginLeft', 'marginRight'].forEach((prop) => {
        if (style[prop] && isUnsetParagraphMargin(style[prop])) {
          style[prop] = ''
          style.removeProperty(prop.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`))
          changed = true
        }
      })
      if (!String(el.getAttribute('style') || '').trim()) {
        el.removeAttribute('style')
      }
    })
    return changed ? root.innerHTML : html
  } catch {
    return html
  }
}

/** Word 单倍行距清掉，让门户 CSS 的 1.8 生效；工具栏 1.5 / 2.0 保留 */
function upgradeLegacyWordLineHeightInHtml(html) {
  if (!html || !/line-height/i.test(html)) return html
  try {
    const parsed = parseHtmlFragment(html, '__lh_up')
    if (!parsed?.root) return html
    const { root } = parsed
    let changed = false
    root.querySelectorAll('[style]').forEach((el) => {
      const style = el.style
      if (!style?.lineHeight || !isWordDefaultLineHeight(style.lineHeight)) return
      style.lineHeight = ''
      changed = true
      if (!String(el.getAttribute('style') || '').trim()) {
        el.removeAttribute('style')
      }
    })
    return changed ? root.innerHTML : html
  } catch {
    return html
  }
}

/** 修复 JSON 文档中将 video 标签误存为纯文本的段落 */
function repairVideoInJson(doc) {
  if (!doc?.content?.length) return doc

  const content = doc.content.flatMap((node) => {
    if (node.type === 'paragraph' && node.content?.length === 1) {
      const textNode = node.content[0]
      if (textNode.type === 'text' && !textNode.marks?.length) {
        const match = textNode.text?.trim().match(VIDEO_TEXT_RE)
        if (match) {
          return [{ type: 'video', attrs: { src: match[1], controls: true } }]
        }
      }
    }
    if (node.content?.length) {
      return [repairVideoInJson(node)]
    }
    return [node]
  })

  return { ...doc, content }
}

function isInlineImageJsonNode(node) {
  return node?.type === 'inlineImage' || (node?.type === 'image' && node.attrs?.align === 'inline')
}

function isJsonHardBreak(node) {
  return node?.type === 'hardBreak'
}

function isJsonWhitespaceText(node) {
  return node?.type === 'text' && !String(node.text || '').trim()
}

// 行内上下文节点：其 content 里的行内图不需要（也不能）再包段落
const TEXTBLOCK_JSON_TYPES = new Set(['paragraph', 'heading'])

function isInlineImageOnlyJsonParagraph(node) {
  if (node?.type !== 'paragraph') return false
  const children = node.content || []
  if (!children.length) return false
  let hasImage = false
  for (const child of children) {
    if (isInlineImageJsonNode(child)) {
      hasImage = true
      continue
    }
    if (isJsonHardBreak(child) || isJsonWhitespaceText(child)) continue
    return false
  }
  return hasImage
}

function isPhrasingJsonParagraph(node) {
  if (node?.type !== 'paragraph') return false
  const children = node.content || []
  let hasText = false
  for (const child of children) {
    if (isInlineImageJsonNode(child)) return false
    if (isJsonHardBreak(child) || isJsonWhitespaceText(child)) continue
    if (child?.type === 'text') {
      hasText = true
      continue
    }
    return false
  }
  return hasText
}

function pickParagraphAttrs(first, second) {
  if (first?.textAlign) return first
  if (second?.textAlign) return second
  return first || second || undefined
}

function withoutEdgeBreaks(nodes = []) {
  return nodes.filter((node) => !isJsonHardBreak(node) && !isJsonWhitespaceText(node))
}

/**
 * 兼容旧数据：块级「段落 + 行内图 + 段落」合并为同一段落，
 * align=inline 的 image 转为 inlineImage；顶层行内图包进段落。
 * 「仅含行内图的段 + 下一段文字」也合成一段，避免门户上下排列。
 */
function repairImageInJson(doc) {
  if (!doc?.content?.length) return doc

  const toInline = (node) => ({
    type: 'inlineImage',
    attrs: { ...(node.attrs || {}), align: 'inline' },
  })

  const mapNodes = (nodes = []) => nodes.map((node) => {
    if (node.type === 'image' && node.attrs?.align === 'inline') return toInline(node)
    if (node.content?.length) return { ...node, content: mapNodes(node.content) }
    return node
  })

  const content = []
  const nodes = mapNodes(doc.content)
  let i = 0
  while (i < nodes.length) {
    const curr = nodes[i]
    const next = nodes[i + 1]
    const after = nodes[i + 2]

    if (
      curr?.type === 'paragraph'
      && (next?.type === 'inlineImage' || (next?.type === 'image' && next.attrs?.align === 'inline'))
      && after?.type === 'paragraph'
    ) {
      content.push({
        type: 'paragraph',
        attrs: pickParagraphAttrs(curr.attrs, after.attrs),
        content: [
          ...(curr.content || []),
          next.type === 'inlineImage' ? next : toInline(next),
          ...(after.content || []),
        ],
      })
      i += 3
      continue
    }

    if (isInlineImageOnlyJsonParagraph(curr) && isPhrasingJsonParagraph(next)) {
      const images = (curr.content || []).map((child) => (
        isInlineImageJsonNode(child) ? (child.type === 'inlineImage' ? child : toInline(child)) : child
      ))
      content.push({
        type: 'paragraph',
        attrs: pickParagraphAttrs(curr.attrs, next.attrs),
        content: [...withoutEdgeBreaks(images), ...withoutEdgeBreaks(next.content || [])],
      })
      i += 2
      continue
    }

    if (curr?.type === 'inlineImage' || (curr?.type === 'image' && curr.attrs?.align === 'inline')) {
      content.push({
        type: 'paragraph',
        content: [curr.type === 'inlineImage' ? curr : toInline(curr)],
      })
      i += 1
      continue
    }

    // 段落 / 标题内部是行内上下文，行内图本就该留在原处，
    // 递归进去会把它再包一层 paragraph，渲染出非法的 <p><p></p></p>
    if (curr?.content?.length && !TEXTBLOCK_JSON_TYPES.has(curr.type)) {
      content.push(repairImageInJson(curr))
    } else {
      content.push(curr)
    }
    i += 1
  }

  return { ...doc, content }
}

/** HTML 转义特殊字符 */
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const TRAILING_LINE_SPACES = /[ \t\u00a0\u3000]+$/
const SPACES_BEFORE_NEWLINE = /[ \t\u00a0\u3000]+(?=\r?\n)/g

function trimLineTrailingSpaces(text) {
  return String(text ?? '').replace(/[ \t\u00a0\u3000]+$/gm, '')
}

const BLOCK_TRIM_PARENTS = new Set([
  'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'LI', 'TD', 'TH', 'BLOCKQUOTE', 'DIV', 'PRE',
  'FIGCAPTION', 'DT', 'DD', 'SECTION', 'ARTICLE',
  'UL', 'OL', 'TABLE', 'THEAD', 'TBODY', 'TR',
])

function isIgnorableWhitespaceText(node) {
  return node?.nodeType === 3 && !String(node.nodeValue || '').replace(/[\s\u00a0\u3000]/g, '')
}

/** 行尾才删空格；加粗/下划线/链接等行内标签里的空格要保留 */
function shouldTrimTextNodeEnd(node) {
  let current = node
  while (current) {
    let next = current.nextSibling
    while (isIgnorableWhitespaceText(next)) next = next.nextSibling
    if (next) {
      if (next.nodeType !== 1) return false
      return next.nodeName === 'BR' || next.nodeName === 'HR'
    }
    const parent = current.parentElement
    if (!parent) return true
    if (BLOCK_TRIM_PARENTS.has(parent.nodeName) || parent.id === '__trim_root') return true
    current = parent
  }
  return true
}

/** 段落经 trim 后若被掏空，补回 <br>，避免预览/门户空行塌掉 */
function restoreEmptyParagraphBreaks(root) {
  if (!root) return
  const doc = root.ownerDocument
  if (!doc) return
  const paragraphs = root.querySelectorAll('p')
  for (const p of paragraphs) {
    if (p.querySelector('*')) continue
    const text = String(p.textContent || '').replace(/[\s\u00a0\u3000]/g, '')
    if (text) continue
    while (p.firstChild) p.removeChild(p.firstChild)
    p.appendChild(doc.createElement('br'))
  }
}

function trimLineTrailingSpacesInDocument(root) {
  if (!root) return
  const doc = root.ownerDocument
  if (!doc) return
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes = []
  while (walker.nextNode()) nodes.push(walker.currentNode)
  for (const node of nodes) {
    let text = node.nodeValue || ''
    text = text.replace(SPACES_BEFORE_NEWLINE, '')
    if (shouldTrimTextNodeEnd(node)) text = text.replace(TRAILING_LINE_SPACES, '')
    if (text !== node.nodeValue) node.nodeValue = text
  }
  restoreEmptyParagraphBreaks(root)
}

function trimLineTrailingSpacesInHtml(html) {
  const source = String(html ?? '')
  if (!source) return ''
  const parsed = parseHtmlFragment(source, '__trim_root')
  if (!parsed?.root) return trimLineTrailingSpaces(source)
  trimLineTrailingSpacesInDocument(parsed.root)
  return parsed.root.innerHTML
}

/** 连续空格、首尾空格、不间断空格、全角空格写入 HTML 后不被浏览器折叠 */
function preserveVisibleSpaces(text) {
  return text
    .replace(/\u00a0/g, '&nbsp;')
    .replace(/\u3000/g, '&#12288;')
    .replace(/^ /g, '&nbsp;')
    .replace(/ $/g, '&nbsp;')
    .replace(/ {2,}/g, (spaces) => ` ${'&nbsp;'.repeat(spaces.length - 1)}`)
}

/** 转义正文文本，并保留编辑器里输入的空格（仅去掉换行前的行尾空格，避免误删加粗前的空格） */
function escapeTextHtml(text) {
  return preserveVisibleSpaces(escapeHtml(String(text ?? '').replace(SPACES_BEFORE_NEWLINE, '')))
}

/** 格式化尺寸值，无单位时默认追加 px */
function formatSize(value, fallback = '') {
  if (value === null || value === undefined || value === '') return fallback
  const text = String(value).trim()
  if (!text) return fallback
  return /[a-z%]$/i.test(text) ? text : `${text}px`
}

/** 排除 width:auto 等默认值，避免无尺寸行内图被误标为可缩放 */
function isExplicitInlineSize(width, height) {
  const real = (value) => {
    if (value == null || value === '') return false
    const text = String(value).trim().toLowerCase()
    return Boolean(text) && text !== 'auto'
  }
  return real(width) || real(height)
}

/** 行内图 HTML style：无尺寸=图标高度；有宽高=可并排缩放 */
function buildInlineImageHtmlStyle(attrs = {}) {
  const parts = [
    'display:inline-block',
    'vertical-align:middle',
    'margin:0 0.15em',
    'max-width:100%',
    'float:none',
    'clear:none',
  ]
  if (isExplicitInlineSize(attrs.width, attrs.height)) {
    if (isExplicitInlineSize(attrs.width, '')) parts.push(`width:${formatSize(attrs.width)}`)
    if (isExplicitInlineSize('', attrs.height)) parts.push(`height:${formatSize(attrs.height)}`, 'object-fit:contain')
    else parts.push('height:auto')
  } else {
    parts.push('max-height:1.8em', 'width:auto', 'height:auto')
  }
  return parts.join(';')
}

/**
 * 兜底的 bodyHtml 里，绝对定位图片会脱离文档流导致与文字重叠。
 * 统一将其转为正常块级元素。
 */
function neutralizeAbsoluteImages(html) {
  if (!html || !/position\s*:\s*absolute/i.test(html)) return html
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    if (!/position\s*:\s*absolute/i.test(tag)) return tag
    return tag.replace(/style=(["'])([\s\S]*?)\1/i, (full, quote, style) => {
      const cleaned = style
        .split(';')
        .map((rule) => rule.trim())
        .filter(Boolean)
        .filter((rule) => !/^(position|top|left|right|bottom|z-index|float)\s*:/i.test(rule))
        .concat(['display:block', 'clear:both', 'max-width:100%'])
        .join(';')
      return `style=${quote}${cleaned}${quote}`
    })
  })
}

/** 将自由定位图片包装为块级元素并规范化样式 */
function wrapFreeImagesInHtml(html) {
  if (!html || !/data-align=["']free["']/i.test(html)) return html
  return html.replace(/<img\b([^>]*?\bdata-align=["']free["'][^>]*?)>/gi, (full, attrs) => {
    const styleMatch = String(attrs).match(/style=["']([^"']*)["']/i)
    const styleText = styleMatch?.[1] || ''
    const widthFromStyle = styleText.match(/(?:^|;)\s*width\s*:\s*([^;]+)/i)?.[1]?.trim()
    const widthFromAttr = String(attrs).match(/\bwidth=["']([^"']+)["']/i)?.[1]?.trim()
    const srcMatch = attrs.match(/\bsrc=["']([^"']+)["']/i)
    const altMatch = attrs.match(/\balt=["']([^"']*)["']/i)

    const src = srcMatch?.[1] || ''
    const alt = escapeHtml(altMatch?.[1] || '')
    const styles = ['max-width:100%', 'display:block', 'clear:both', 'float:none', 'height:auto']
    const width = widthFromStyle || widthFromAttr
    if (width) styles.push(`width:${formatSize(width)}`)
    styles.push('margin:8px auto')
    return `<img class="editor-image" src="${escapeHtml(src)}" alt="${alt}" data-align="free" style="${styles.join(';')}" />`
  })
}

/** 根据块级属性生成 style 属性字符串 */
function blockStyleAttr(attrs = {}) {
  const style = []
  if (attrs.textAlign) style.push(`text-align:${attrs.textAlign}`)
  if (attrs.lineHeight && !isWordDefaultLineHeight(attrs.lineHeight)) {
    style.push(`line-height:${attrs.lineHeight}`)
  }
  const indentLevel = Number(attrs.indent) || 0
  if (indentLevel > 0) style.push(`text-indent:${indentLevel * 2}em`)
  if (attrs.paragraphSpacing) {
    const spacing = normalizeParagraphSpacingValue(attrs.paragraphSpacing)
    if (spacing) {
      const [marginTop = '', marginBottom = ''] = spacing.split('|')
      if (marginTop) style.push(`margin-top:${marginTop}`)
      if (marginBottom) style.push(`margin-bottom:${marginBottom}`)
    }
  }
  return style.length ? ` style="${style.join(';')}"` : ''
}

/** 将文本 marks（加粗、链接、颜色等）渲染为 HTML */
function renderMarks(text, marks = []) {
  const linkMark = (marks || []).find((mark) => mark?.type === 'link')
  const source = linkMark && (isProductDeviceLink(linkMark.attrs || {}) || isProductCategoryLink(linkMark.attrs || {}))
    ? stripProductLinkPrefix(text)
    : text
  let result = escapeTextHtml(source)
  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        result = `<strong>${result}</strong>`
        break
      case 'italic':
        result = `<em>${result}</em>`
        break
      case 'underline':
        result = `<u>${result}</u>`
        break
      case 'strike':
        result = `<s>${result}</s>`
        break
      case 'subscript':
        result = `<sub>${result}</sub>`
        break
      case 'superscript':
        result = `<sup>${result}</sup>`
        break
      case 'link': {
        const href = escapeHtml(mark.attrs?.href || '#')
        const isAttachment = mark.attrs?.['data-link-type'] === 'attachment'
        const isCopied = isCopiedAbsoluteHref(mark.attrs?.href, mark.attrs?.['data-link-type'])
        const targetValue = isAttachment ? '_blank' : mark.attrs?.target
        const target = targetValue ? ` target="${escapeHtml(targetValue)}"` : ''
        const rel = (isAttachment || isCopied) ? ' rel="noopener noreferrer"' : ''
        const linkType = mark.attrs?.['data-link-type']
          ? ` data-link-type="${escapeHtml(mark.attrs['data-link-type'])}"`
          : ''
        const productId = mark.attrs?.['data-product-id']
          ? ` data-product-id="${escapeHtml(mark.attrs['data-product-id'])}"`
          : ''
        const categoryId = mark.attrs?.['data-category-id']
          ? ` data-category-id="${escapeHtml(mark.attrs['data-category-id'])}"`
          : ''
        const cardTitle = mark.attrs?.['data-card-title']
          ? ` data-card-title="${escapeHtml(mark.attrs['data-card-title'])}"`
          : ''
        const cardImage = mark.attrs?.['data-card-image']
          ? ` data-card-image="${escapeHtml(mark.attrs['data-card-image'])}"`
          : ''
        const cardCerts = mark.attrs?.['data-card-certs']
          ? ` data-card-certs="${escapeHtml(mark.attrs['data-card-certs'])}"`
          : ''
        const cardSubtitle = mark.attrs?.['data-card-subtitle']
          ? ` data-card-subtitle="${escapeHtml(mark.attrs['data-card-subtitle'])}"`
          : ''
        const attachmentName = mark.attrs?.['data-attachment-name']
          ? ` data-attachment-name="${escapeHtml(mark.attrs['data-attachment-name'])}"`
          : ''
        const relatedCard = mark.attrs?.['data-related-card']
          ? ` data-related-card="${escapeHtml(mark.attrs['data-related-card'])}"`
          : ''
        const showText = mark.attrs?.['data-show-text']
          ? ` data-show-text="${escapeHtml(mark.attrs['data-show-text'])}"`
          : ''
        const hideText = String(mark.attrs?.['data-show-text'] || '').trim() === '0'
        const hidden = hideText ? ' hidden' : ''
        result = `<a href="${href}"${target}${rel}${linkType}${productId}${categoryId}${cardTitle}${cardImage}${cardCerts}${cardSubtitle}${attachmentName}${relatedCard}${showText}${hidden}>${result}</a>`
        break
      }
      case 'textStyle': {
        const styles = []
        if (mark.attrs?.color) styles.push(`color:${mark.attrs.color}`)
        if (mark.attrs?.fontSize) styles.push(`font-size:${mark.attrs.fontSize}`)
        if (mark.attrs?.lineHeight && !isWordDefaultLineHeight(mark.attrs.lineHeight)) {
          styles.push(`line-height:${mark.attrs.lineHeight}`)
        }
        if (mark.attrs?.letterSpacing) styles.push(`letter-spacing:${mark.attrs.letterSpacing}`)
        if (styles.length) result = `<span style="${styles.join(';')}">${result}</span>`
        break
      }
      case 'highlight': {
        const color = mark.attrs?.color || '#ffff00'
        result = `<mark style="background-color:${color}">${result}</mark>`
        break
      }
      default:
        break
    }
  }
  return result
}

/** 组装表格单元格属性（合并单元格等） */
function tableCellAttrString(attrs = {}) {
  const parts = []
  const colspan = Number(attrs.colspan) || 1
  const rowspan = Number(attrs.rowspan) || 1
  if (colspan > 1) parts.push(`colspan="${colspan}"`)
  if (rowspan > 1) parts.push(`rowspan="${rowspan}"`)
  if (Array.isArray(attrs.colwidth) && attrs.colwidth.length) {
    parts.push(`colwidth="${attrs.colwidth.filter((n) => n != null).join(',')}"`)
  }
  const styles = []
  const vAlign = attrs.verticalAlign === 'top' || attrs.verticalAlign === 'bottom' || attrs.verticalAlign === 'middle'
    ? attrs.verticalAlign
    : (attrs.verticalAlign ? 'middle' : '')
  if (vAlign) {
    parts.push(`data-vertical-align="${vAlign}"`)
    styles.push(`vertical-align:${vAlign}`)
  }
  const hAlign = attrs.textAlign === 'center' || attrs.textAlign === 'right' || attrs.textAlign === 'left'
    ? attrs.textAlign
    : (attrs.textAlign ? 'left' : '')
  if (hAlign) {
    parts.push(`data-text-align="${hAlign}"`)
    styles.push(`text-align:${hAlign}`)
  }
  if (styles.length) parts.push(`style="${styles.join(';')}"`)
  return parts.length ? ` ${parts.join(' ')}` : ''
}

/** 递归将 TipTap/ProseMirror JSON 节点渲染为 HTML */
function renderNode(node, options = {}) {
  if (!node) return ''

  if (node.type === 'text') {
    return renderMarks(node.text || '', node.marks || [])
  }

  // inner
  const inner = (node.content || []).map((child) => renderNode(child, options)).join('')
  const attrs = node.attrs || {}

  switch (node.type) {
    case 'doc':
      return inner
    case 'paragraph': {
      const styleAttr = blockStyleAttr(attrs)
      return `<p${styleAttr}>${inner || '<br>'}</p>`
    }
    case 'heading': {
      const level = attrs.level || 2
      const styleAttr = blockStyleAttr(attrs)
      return `<h${level}${styleAttr}>${inner}</h${level}>`
    }
    case 'mathInline':
      return wrapMathHtml(attrs.latex, false)
    case 'mathBlock':
      return wrapMathHtml(attrs.latex, true)
    case 'inlineImage':
    case 'image': {
      const src = resolveFileUrl(attrs.src)
      if (!src) return ''
      const alt = escapeHtml(attrs.alt || '')
      const align = node.type === 'inlineImage' ? 'inline' : (attrs.align || 'center')
      const width = formatSize(attrs.width)
      const height = formatSize(attrs.height)

      if (align === 'float-left' || align === 'float-right') {
        const wrapperStyles = [
          `float:${align === 'float-left' ? 'left' : 'right'}`,
          align === 'float-left' ? 'margin:4px 16px 8px 0' : 'margin:4px 0 8px 16px',
          'clear:none',
          'max-width:100%',
        ]
        if (width) wrapperStyles.push(`width:${width}`)
        const imageStyles = ['width:100%', 'height:auto', 'display:block', 'max-width:100%']
        return `<span class="image-node-wrapper" data-align="${align}" style="${wrapperStyles.join(';')}"><img class="editor-image" src="${escapeHtml(src)}" alt="${alt}" data-align="${align}" style="${imageStyles.join(';')}" /></span>`
      }

      if (align === 'inline') {
        const inlineStyles = buildInlineImageHtmlStyle({ width, height })
        const sizeAttr = isExplicitInlineSize(width, height) ? ' data-inline-size="1"' : ''
        return `<img class="editor-image" src="${escapeHtml(src)}" alt="${alt}" data-align="inline"${sizeAttr} style="${inlineStyles}" />`
      }

      if (align === 'free') {
        const freeStyles = ['max-width:100%', 'display:block', 'clear:both', 'float:none', 'height:auto']
        if (width) freeStyles.push(`width:${width}`)
        freeStyles.push('margin:8px auto')
        return `<img class="editor-image" src="${escapeHtml(src)}" alt="${alt}" data-align="free" style="${freeStyles.join(';')}" />`
      }

      // 与编辑器一致：宽度落在包裹层，图片铺满包裹层
      const wrapperStyles = ['display:block', 'max-width:100%', 'clear:both', 'float:none']
      if (width) wrapperStyles.push(`width:${width}`)
      if (align === 'left') {
        wrapperStyles.push('margin:8px auto 8px 0')
      } else if (align === 'right') {
        wrapperStyles.push('margin:8px 0 8px auto')
      } else {
        wrapperStyles.push('margin:8px auto')
      }

      const imageStyles = ['width:100%', 'display:block', 'max-width:100%']
      if (height) {
        imageStyles.push(`height:${height}`, 'object-fit:contain')
      } else {
        imageStyles.push('height:auto')
      }

      return `<span class="image-node-wrapper" data-align="${align}" style="${wrapperStyles.join(';')}"><img class="editor-image" src="${escapeHtml(src)}" alt="${alt}" data-align="${align}" style="${imageStyles.join(';')}" /></span>`
    }
    case 'video': {
      const src = resolveFileUrl(attrs.src)
      if (!src) return ''
      const style = attrs.style || 'width:100%;max-width:100%;display:block;margin:8px auto;border-radius:4px'
      return `<video src="${escapeHtml(src)}" controls style="${escapeHtml(style)}"></video>`
    }
    case 'bulletList':
      return `<ul>${inner}</ul>`
    case 'orderedList':
      return `<ol>${inner}</ol>`
    case 'listItem':
      return `<li>${inner}</li>`
    case 'blockquote':
      return `<blockquote>${inner}</blockquote>`
    case 'codeBlock':
      return `<pre><code>${inner}</code></pre>`
    case 'hardBreak':
      return '<br>'
    case 'horizontalRule':
      return '<hr>'
    case 'tableTabs':
      return buildTableTabsHtml(node, (child) => renderNode(child, { ...options, skipTableCollapse: true }), {
        colgroupHtml: tableColgroupHtml,
      })
    case 'tableTabPanel':
      return `<section class="table-tabs__panel" data-type="table-tab-panel" data-title="${escapeHtml(attrs.title || '表格1')}">${inner}</section>`
    case 'table': {
      const style = normalizeTableStyle(attrs.tableStyle)
      const className = tableStyleClassName(style)
      const tableHtml = `<table class="${className}" data-table-style="${style}" style="width:100%;max-width:100%;min-width:0">${tableColgroupHtml(node)}<tbody>${inner}</tbody></table>`
      if (options.skipTableCollapse) return tableHtml
      return wrapTableForCollapse(tableHtml, countTableRows(node), options.collapseRows ?? DEFAULT_COLLAPSE_ROWS)
    }
    case 'tableRow':
      return `<tr>${inner}</tr>`
    case 'tableHeader':
      return `<th${tableCellAttrString(attrs)}>${inner || '<p></p>'}</th>`
    case 'tableCell':
      return `<td${tableCellAttrString(attrs)}>${inner || '<p></p>'}</td>`
    case 'hotspotTabs':
      return buildHotspotTabsHtml(attrs, { resolveSrc: resolveFileUrl, anchorIds: options.anchorIds })
    case 'equipmentIntro':
      return buildEquipmentIntroHtml(attrs, { resolveSrc: resolveFileUrl })
    case 'imageGallery':
      return buildImageGalleryHtml(attrs, { resolveSrc: resolveFileUrl })
    default:
      return inner
  }
}

/** 按首行 colwidth 生成百分比 colgroup，避免固定 px 宽导致预览/门户表格挤在左侧 */
function tableColgroupHtml(tableNode) {
  const firstRow = (tableNode?.content || []).find((n) => n.type === 'tableRow')
  if (!firstRow?.content?.length) return ''

  const widths = []
  for (const cell of firstRow.content) {
    if (cell.type !== 'tableCell' && cell.type !== 'tableHeader') continue
    const attrs = cell.attrs || {}
    const colspan = Math.max(1, Number(attrs.colspan) || 1)
    const colwidth = Array.isArray(attrs.colwidth) ? attrs.colwidth : []
    for (let i = 0; i < colspan; i += 1) {
      const w = Number(colwidth[i] || colwidth[0] || 0)
      widths.push(Number.isFinite(w) && w > 0 ? w : 0)
    }
  }
  if (!widths.length) return ''

  const knownSum = widths.reduce((sum, n) => sum + (n > 0 ? n : 0), 0)
  const unknownCount = widths.filter((n) => n <= 0).length
  let resolved = widths
  if (unknownCount > 0) {
    const assumedTableWidth = Math.max(knownSum + unknownCount * 120, 800)
    const remain = Math.max(unknownCount * 48, assumedTableWidth - knownSum)
    const eachUnknown = remain / unknownCount
    resolved = widths.map((w) => (w > 0 ? w : eachUnknown))
  } else if (!(knownSum > 0)) {
    resolved = widths.map(() => 1)
  }

  const total = resolved.reduce((sum, n) => sum + n, 0) || resolved.length
  const cols = resolved
    .map((w) => `<col style="width:${((w / total) * 100).toFixed(4)}%">`)
    .join('')
  return `<colgroup>${cols}</colgroup>`
}

/** 将 bodyJson 文档转为 HTML 字符串 */
function htmlFromJson(bodyJson) {
  if (!bodyJson) return ''
  try {
    const json = typeof bodyJson === 'string' ? JSON.parse(bodyJson) : bodyJson
    upgradeLegacyTablesInJson(json)
    upgradeLegacyParagraphSpacingInJson(json)
    if (json?.content?.length) {
      const repaired = repairImageInJson(repairVideoInJson(json))
      splitSentenceHardBreaksInJson(repaired)
      const anchorIds = new Set()
      return renderNode(repaired, { anchorIds })
    }
  } catch {
    // ignore invalid json
  }
  return ''
}

/** 判断 HTML 是否包含有效内容（排除空段落与 br） */
function isMeaningfulHtml(html) {
  if (!html) return false
  const text = html
    .replace(/<p><\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .trim()
  return text.length > 0
}

/** 从首行单元格 colwidth / 既有 col 提取列宽 */
function collectDisplayColWidths(table) {
  const existingCols = Array.from(table.querySelectorAll(':scope > colgroup > col'))
  if (existingCols.length) {
    const fromCols = existingCols.map((col) => {
      const fromWidth = parseFloat(col.style.width) || 0
      const fromMin = parseFloat(col.style.minWidth) || 0
      const fromAttr = parseFloat(col.getAttribute('width')) || 0
      return fromWidth || fromAttr || fromMin || 0
    })
    if (fromCols.some((n) => n > 0)) return fromCols
  }

  const firstRow = table.querySelector(':scope > tbody > tr, :scope > thead > tr, :scope > tr')
  if (!firstRow) return []

  const widths = []
  firstRow.querySelectorAll(':scope > th, :scope > td').forEach((cell) => {
    const colspan = Math.max(1, parseInt(cell.getAttribute('colspan') || '1', 10) || 1)
    const raw = cell.getAttribute('colwidth') || cell.getAttribute('data-colwidth') || ''
    const parts = String(raw)
      .split(',')
      .map((s) => parseFloat(s))
      .filter((n) => Number.isFinite(n) && n > 0)
    for (let i = 0; i < colspan; i += 1) {
      widths.push(parts[i] || parts[0] || 0)
    }
  })
  return widths
}

/** 展示态去掉表格固定 px 宽，改为百分比铺满 */
function normalizeDisplayTableHtml(html) {
  if (!html || !/<table[\s>]/i.test(html)) return html
  html = upgradeLegacyTablesInHtml(html)
  try {
    const parsed = parseHtmlFragment(html, '__table_root')
    if (!parsed) return html
    const { doc, root } = parsed

    root.querySelectorAll('table').forEach((table) => {
      table.style.removeProperty('width')
      table.style.removeProperty('min-width')
      table.style.removeProperty('max-width')
      table.style.setProperty('width', '100%')

      const widths = collectDisplayColWidths(table)
      if (!widths.length) return

      let colgroup = table.querySelector(':scope > colgroup')
      if (!colgroup) {
        colgroup = doc.createElement('colgroup')
        table.insertBefore(colgroup, table.firstChild)
      }
      while (colgroup.firstChild) colgroup.removeChild(colgroup.firstChild)

      const knownSum = widths.reduce((sum, n) => sum + (n > 0 ? n : 0), 0)
      const unknownCount = widths.filter((n) => n <= 0).length
      let resolved = widths
      if (unknownCount > 0) {
        const assumedTableWidth = Math.max(knownSum + unknownCount * 120, 800)
        const remain = Math.max(unknownCount * 48, assumedTableWidth - knownSum)
        const eachUnknown = remain / unknownCount
        resolved = widths.map((w) => (w > 0 ? w : eachUnknown))
      } else if (!(knownSum > 0)) {
        resolved = widths.map(() => 1)
      }

      const total = resolved.reduce((sum, n) => sum + n, 0) || resolved.length
      resolved.forEach((w) => {
        const col = doc.createElement('col')
        col.style.setProperty('width', `${((w / total) * 100).toFixed(4)}%`)
        colgroup.appendChild(col)
      })
    })

    // 兼容旧 HTML：尚未包裹折叠容器的表格补上折叠
    root.querySelectorAll('table').forEach((table) => {
      if (table.closest('.rich-table-block')) return
      const rows = table.querySelectorAll(':scope > tbody > tr, :scope > thead > tr, :scope > tr')
      const limitAttr = table.closest('.table-tabs')?.getAttribute('data-collapse-rows')
      const limit = Math.max(1, Math.min(100, Number(limitAttr) || DEFAULT_COLLAPSE_ROWS))
      rows.forEach((tr, i) => {
        if (i >= limit) tr.classList.add('is-overflow-row')
      })
      const needsCollapse = rows.length > limit
      const wrap = doc.createElement('div')
      wrap.className = 'rich-table-block'
      wrap.setAttribute('data-collapse-rows', String(limit))
      wrap.setAttribute('data-collapsed', needsCollapse ? 'true' : 'false')
      table.parentNode?.insertBefore(wrap, table)
      wrap.appendChild(table)
      if (needsCollapse) {
        const btn = doc.createElement('button')
        btn.type = 'button'
        btn.className = 'rich-table-toggle'
        btn.setAttribute('aria-expanded', 'false')
        btn.setAttribute('aria-label', getRichContentUiLabels().expandTable)
        btn.innerHTML = '<span class="rich-table-toggle__icon" aria-hidden="true"></span>'
        wrap.appendChild(btn)
      }
    })

    return root.innerHTML
  } catch {
    return html
  }
}

/** 解析正文 HTML：优先 bodyJson，回退 bodyHtml 并修复图片问题 */
export function resolveBodyHtml(bodyHtml, bodyJson) {
  const cleanedHtml = stripDisplayFlagsFromHtml(bodyHtml)
  const fromJson = htmlFromJson(bodyJson)
  if (fromJson) {
    return stripDisplayFlagsFromHtml(upgradeLegacyWordLineHeightInHtml(upgradeLegacyParagraphSpacingInHtml(
      splitSentenceHardBreaksInHtml(trimLineTrailingSpacesInHtml(normalizeDisplayTableHtml(mergeSplitInlineImageHtml(
        stripProductLinkPrefixInHtml(fromJson),
      )))),
    )))
  }
  if (!isMeaningfulHtml(cleanedHtml)) return ''
  return stripDisplayFlagsFromHtml(upgradeLegacyWordLineHeightInHtml(upgradeLegacyParagraphSpacingInHtml(
    splitSentenceHardBreaksInHtml(trimLineTrailingSpacesInHtml(normalizeDisplayTableHtml(
      repairImageGalleryHtml(repairEquipmentIntroHtml(repairHotspotHtml(
        mergeSplitInlineImageHtml(
          repairInlineImageStyles(wrapFreeImagesInHtml(neutralizeAbsoluteImages(
            stripProductLinkPrefixInHtml(cleanedHtml),
          ))),
        ),
      ))),
    ))),
  )))
}

/** 纠正行内图被写成 display:block / clear:both 的错误样式 */
function repairInlineImageStyles(html) {
  if (!html || !/data-align=["']inline["']/i.test(html)) return html
  return html.replace(/<img\b([^>]*?\bdata-align=["']inline["'][^>]*?)>/gi, (full, attrs) => {
    const styleMatch = String(attrs).match(/style=(["'])([\s\S]*?)\1/i)
    const styleText = styleMatch?.[2] || ''
    const width =
      styleText.match(/(?:^|;)\s*width\s*:\s*([^;]+)/i)?.[1]?.trim()
      || String(attrs).match(/\bwidth=["']([^"']+)["']/i)?.[1]?.trim()
      || null
    const height =
      styleText.match(/(?:^|;)\s*height\s*:\s*([^;]+)/i)?.[1]?.trim()
      || String(attrs).match(/\bheight=["']([^"']+)["']/i)?.[1]?.trim()
      || null
    const cleaned = String(attrs)
      .replace(/\sstyle=(["'])[\s\S]*?\1/i, '')
      .replace(/\sstyle=(["'])[\s\S]*?\1/i, '')
      .replace(/\sdata-inline-size=["'][^"']*["']/i, '')
    const sizeAttr = isExplicitInlineSize(width, height) ? ' data-inline-size="1"' : ''
    const style = buildInlineImageHtmlStyle({ width, height })
    return `<img${cleaned}${sizeAttr} style="${style}">`
  })
}

/** 去掉行内图两侧的 HTML 换行，避免 pre-wrap 把图文拆成两行 */
function trimInlineImageAdjacentWhitespace(html) {
  if (!html || !/data-align=["']inline["']/i.test(html)) return html
  return html
    .replace(/(<img\b[^>]*?\bdata-align=["']inline["'][^>]*?>)[\t ]*(?:\r?\n)+[\t ]*/gi, '$1')
    .replace(/[\t ]*(?:\r?\n)+[\t ]*(<img\b[^>]*?\bdata-align=["']inline["'][^>]*?>)/gi, '$1')
}

function stripBrAroundInlineImages(html) {
  return String(html || '')
    .replace(/(<img\b[^>]*?\bdata-align=["']inline["'][^>]*?>)\s*(?:<br\s*\/?>\s*)+/gi, '$1')
    .replace(/(?:<br\s*\/?>\s*)+(<img\b[^>]*?\bdata-align=["']inline["'][^>]*?>)/gi, '$1')
}

function paragraphHasOnlyInlineImages(inner) {
  if (!/<img\b[^>]*?\bdata-align=["']inline["']/i.test(inner)) return false
  if (/<(?:ul|ol|table|blockquote|pre|div|h[1-6])\b/i.test(inner)) return false
  if (/<img\b(?![^>]*\bdata-align=["']inline["'])/i.test(inner)) return false
  const rest = String(inner)
    .replace(/<img\b[^>]*?\bdata-align=["']inline["'][^>]*?>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/[\s\u00a0\u3000]/g, '')
  return !rest
}

function paragraphIsPhrasingText(inner) {
  if (/<(?:ul|ol|table|blockquote|pre|div|h[1-6]|img)\b/i.test(inner)) return false
  const text = String(inner)
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/[\s\u00a0\u3000]/g, '')
  return Boolean(text)
}

function pickParagraphHtmlAttrs(first, second) {
  const a = first || ''
  const b = second || ''
  if (/text-align\s*:\s*(center|right|left|justify)/i.test(a)) return a
  if (/text-align\s*:\s*(center|right|left|justify)/i.test(b)) return b
  return a
}

/** 将错误拆成多段的行内图 HTML 重新拼回同一段落 */
function mergeSplitInlineImageHtml(html) {
  if (!html || !/data-align=["']inline["']/i.test(html)) return html
  let merged = html.replace(
    /<p(\b[^>]*)>([\s\S]*?)<\/p>\s*(<img\b[^>]*?\bdata-align=["']inline["'][^>]*?>)\s*<p(\b[^>]*)>([\s\S]*?)<\/p>/gi,
    (full, p1Attrs, before, img, p2Attrs, after) => {
      if (/<(?:ul|ol|table|blockquote|pre|div|h[1-6])\b/i.test(before + after)) return full
      return `<p${pickParagraphHtmlAttrs(p1Attrs, p2Attrs)}>${before}${img}${after}</p>`
    },
  )

  let prev = ''
  while (prev !== merged) {
    prev = merged
    merged = merged.replace(
      /<p(\b[^>]*)>([\s\S]*?)<\/p>\s*<p(\b[^>]*)>([\s\S]*?)<\/p>/gi,
      (full, p1Attrs, inner1, p2Attrs, inner2) => {
        if (!paragraphHasOnlyInlineImages(inner1) || !paragraphIsPhrasingText(inner2)) return full
        return `<p${pickParagraphHtmlAttrs(p1Attrs, p2Attrs)}>${stripBrAroundInlineImages(`${inner1}${inner2}`)}</p>`
      },
    )
  }

  merged = stripBrAroundInlineImages(merged)
  return trimInlineImageAdjacentWhitespace(merged)
}
